"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, Mail } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function NewsletterSignUp() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  return <section id="newsletter-signup" className="w-full bg-dark-background text-foreground-dark py-16 px-4 md:py-24">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div className="space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold">
              Stay in the Loop
            </h2>
            <p className="text-lg text-muted">
              Subscribe to our newsletter and get the latest updates, exclusive
              offers, and insights delivered straight to your inbox.
            </p>
          </div>

          {}
          <FormProvider className="w-full" formId="newsletter_signup_home2" sectionName="newsletter-signup" defaultValues={{
          first_name: "",
          email: "",
          consent: false
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          },
          consent: {
            required: true
          }
        }} submitText="Subscribe Now" successMessage="Welcome aboard! Check your inbox for a confirmation email." errorMessage="Oops! Something went wrong. Please try again.">
            <NewsletterForm animationRef={ref} isVisible={isVisible} />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function NewsletterForm({
  animationRef,
  isVisible
}: {
  animationRef: React.RefObject<HTMLDivElement>;
  isVisible: boolean;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent/10 border border-accent rounded-lg animate-fade-in-up">
        <div className="flex items-start gap-3">
          <Mail className="size-6 text-accent shrink-0 mt-1" />
          <div>
            <p className="text-foreground-dark font-semibold mb-2">Success!</p>
            <p className="text-muted mb-4">{successMessage}</p>
            <Button onClick={resetForm} variant="outline" size="sm">
              Subscribe Another Email
            </Button>
          </div>
        </div>
      </div>;
  }
  return <div ref={animationRef}>
      <Form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {}
          <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <FormField control={control} name="first_name" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground-dark">First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane" {...field} className="bg-secondary-light border-border text-foreground-dark placeholder:text-muted" />
                  </FormControl>
                  <FormDescription className="text-muted text-sm">
                    Optional – we'll address you by name in our emails.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          <div className={`transition-all duration-[var(--duration-medium)] delay-75 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <FormField control={control} name="email" rules={{
            required: "Email address is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground-dark">Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@example.com" {...field} className="bg-secondary-light border-border text-foreground-dark placeholder:text-muted" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          <div className={`transition-all duration-[var(--duration-medium)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <FormField control={control} name="consent" rules={{
            required: "You must agree to receive the newsletter"
          }} render={({
            field
          }) => <FormItem>
                  <div className="flex items-start gap-3">
                    <FormControl>
                      <input type="checkbox" checked={field.value} onChange={field.onChange} onBlur={field.onBlur} className="mt-1 size-4 rounded border-border bg-secondary-light text-accent focus:ring-2 focus:ring-accent cursor-pointer" />
                    </FormControl>
                    <div className="flex-1">
                      <FormLabel className="text-foreground-dark font-normal cursor-pointer">
                        I agree to receive the newsletter and promotional updates.
                      </FormLabel>
                      <FormDescription className="text-muted text-sm mt-1">
                        We respect your privacy. You can unsubscribe at any time.
                      </FormDescription>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          {submitError && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm">{submitError}</p>
            </div>}

          {}
          <div className={`transition-all duration-[var(--duration-medium)] delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-foreground-accent hover:bg-accent-hover hover:-translate-y-0.5 hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus-ring">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Subscribing...
                </> : "Subscribe Now"}
            </Button>
          </div>
        </div>
      </Form>
    </div>;
}